import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "../hooks/useIsMobile";

export type SheetSize = "sm" | "md" | "lg" | "xl" | "5xl";
export type SheetVariant = "auto" | "modal" | "sheet";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  /** Optional header title. When a string, it also becomes the dialog aria-label. */
  title?: ReactNode;
  /**
   * `auto` (default) → bottom sheet on phones, centered modal on tablet/desktop.
   * `modal` forces the centered box; `sheet` forces the bottom sheet.
   */
  variant?: SheetVariant;
  /** max-width of the centered (desktop) variant. Ignored for the bottom sheet. */
  size?: SheetSize;
  /** Sticky action row pinned to the bottom of the panel. */
  footer?: ReactNode;
  /** Backdrop / Esc / close-button close. Default true. */
  dismissible?: boolean;
  /** Skip the built-in title/close header (caller renders its own). Backdrop
   *  and Esc close still work. */
  hideHeader?: boolean;
  testId?: string;
  children: ReactNode;
}

const SIZE_MAX_W: Record<SheetSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-2xl",
  // Wide comparison layouts (e.g. preset compare) — desktop-only width; the
  // phone variant is a full-width bottom sheet regardless.
  "5xl": "max-w-5xl",
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Canonical mobile-aware modal. Codifies the app's `fixed inset-0 … max-w-*`
 * modal box (the RESPONSIVE.md contract) as one component and adds a
 * bottom-sheet variant for phones, focus trap, Esc/backdrop close, body-scroll
 * lock, and safe-area padding. Reused by every form/dialog/popover on mobile.
 */
export function Sheet({
  open,
  onClose,
  title,
  variant = "auto",
  size = "md",
  footer,
  dismissible = true,
  hideHeader = false,
  testId,
  children,
}: SheetProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const asSheet = variant === "sheet" || (variant === "auto" && isMobile);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const { body } = document;
    const prevOverflow = body.style.overflow;
    body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && dismissible) {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const target = panel.querySelector<HTMLElement>(FOCUSABLE) ?? panel;
      target.focus();
    });

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      cancelAnimationFrame(raf);
      body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open, dismissible, onClose]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex bg-black/60 ${
        asSheet ? "items-end justify-center" : "items-center justify-center p-4"
      }`}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
        tabIndex={-1}
        data-testid={testId}
        className={
          asSheet
            ? "flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl ring-1 ring-slate-200/80 dark:bg-slate-800 dark:ring-slate-700/80"
            : `flex max-h-[90vh] w-full ${SIZE_MAX_W[size]} flex-col rounded-xl bg-white shadow-2xl ring-1 ring-slate-200/80 dark:bg-slate-800 dark:ring-slate-700/80`
        }
      >
        {asSheet && (
          <div className="flex justify-center pb-1 pt-3" aria-hidden="true">
            <span className="h-1.5 w-10 rounded-full bg-slate-300 dark:bg-slate-600" />
          </div>
        )}
        {!hideHeader && (title || dismissible) && (
          <div className="flex items-center justify-between gap-3 px-5 pb-3 pt-4">
            {title ? (
              <h2 className="text-lg font-bold text-fg-primary">{title}</h2>
            ) : (
              <span aria-hidden="true" />
            )}
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t("common.close", "Close")}
                className="tap-target -mr-2 inline-flex items-center justify-center rounded-lg p-2 text-fg-muted hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
        {footer && (
          <div className="border-t border-slate-200 px-5 py-3 dark:border-slate-700">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
